import React, { useState, useRef, useEffect, useCallback } from 'react';
import { SurveyConfig, SurveyQuestion, SurveyResponse } from '../../config/surveys/schema';
import { getSurveyConfig } from '../../config/surveys';
import EmojiScaleQuestion from './questions/EmojiScaleQuestion';
import TextareaQuestion from './questions/TextareaQuestion';
import SurveySidebar from './sidebar/SurveySidebar';
import { generateAllTestAnswers, isDebugMode } from '../../utils/surveyAutoComplete';
import './SurveyRenderer.css';
import './sidebar/SurveySidebar.css';

interface SurveyRendererProps {
  surveyId: string;
  onComplete: (response: SurveyResponse) => void;
  onProgress?: (progress: { current: number; total: number; percent: number }) => void;
}

const SurveyRenderer: React.FC<SurveyRendererProps> = ({
  surveyId,
  onComplete,
  onProgress,
}) => {
  const [config, setConfig] = useState<SurveyConfig | null>(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [unlockedSections, setUnlockedSections] = useState(new Set([0]));
  const [pendingScrollTarget, setPendingScrollTarget] = useState<number | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);

  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isScrollingProgrammatically = useRef(false);

  // Load survey config
  useEffect(() => {
    const surveyConfig = getSurveyConfig(surveyId);
    if (surveyConfig) {
      setConfig(surveyConfig);
    }
  }, [surveyId]);

  // Total sections: 0=Intro, 1..N=Questions, N+1=Complete
  const totalSections = config ? config.questions.length + 2 : 0;

  // Intersection Observer for tracking current section
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isScrollingProgrammatically.current) {
            const sectionIndex = sectionRefs.current.findIndex(ref => ref === entry.target);
            if (sectionIndex !== -1) {
              setCurrentSection(sectionIndex);
            }
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: '-10% 0px -10% 0px'
      }
    );

    sectionRefs.current.forEach(ref => {
      if (ref) observer.observe(ref);
    });

    return () => {
      sectionRefs.current.forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [config]);

  // Prevent scrolling in main content (but allow in sidebar, and allow all on mobile)
  useEffect(() => {
    const isMobile = () => window.innerWidth <= 768;

    const isInSidebar = (event: Event): boolean => {
      if (event.target instanceof HTMLElement) {
        return !!event.target.closest('.survey-sidebar');
      }
      return false;
    };

    const getEventPath = (event: Event): EventTarget[] => (
      typeof event.composedPath === 'function' ? event.composedPath() : []
    );

    const findScrollableContainer = (event: Event): HTMLElement | null => {
      const path = getEventPath(event);
      for (const entry of path) {
        if (entry instanceof HTMLElement && entry.classList.contains('section-content')) {
          if (entry.scrollHeight > entry.clientHeight + 1) {
            return entry;
          }
        }
      }
      if (event.target instanceof HTMLElement) {
        const fallback = event.target.closest('.section-content');
        if (fallback instanceof HTMLElement && fallback.scrollHeight > fallback.clientHeight + 1) {
          return fallback;
        }
      }
      return null;
    };

    const isInteractivePath = (event: Event): boolean => {
      const path = getEventPath(event);
      for (const entry of path) {
        if (entry instanceof HTMLElement) {
          if (entry.isContentEditable || entry.matches(`button, input, select, textarea, label, a, [role='button']`)) {
            return true;
          }
        }
      }
      if (event.target instanceof HTMLElement) {
        if (event.target.closest(`button, input, select, textarea, label, a, [role='button']`)) {
          return true;
        }
      }
      return false;
    };

    const preventWheel = (e: WheelEvent) => {
      // Allow all scrolling on mobile or in sidebar
      if (isMobile() || isInSidebar(e)) return;

      if (!isScrollingProgrammatically.current) {
        if (findScrollableContainer(e) || isInteractivePath(e)) return;
        e.preventDefault();
      }
    };

    const preventTouch = (e: TouchEvent) => {
      // Allow all scrolling on mobile or in sidebar
      if (isMobile() || isInSidebar(e)) return;

      if (!isScrollingProgrammatically.current) {
        if (findScrollableContainer(e) || isInteractivePath(e)) return;
        e.preventDefault();
      }
    };

    const preventKeyboard = (e: KeyboardEvent) => {
      // Allow all scrolling on mobile or in sidebar
      if (isMobile() || isInSidebar(e)) return;

      if (!isScrollingProgrammatically.current && !isInteractivePath(e)) {
        const scrollKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Space', 'PageUp', 'PageDown', 'Home', 'End'];
        if (scrollKeys.includes(e.code)) {
          e.preventDefault();
        }
      }
    };

    document.addEventListener('wheel', preventWheel, { passive: false });
    document.addEventListener('touchstart', preventTouch, { passive: false });
    document.addEventListener('touchmove', preventTouch, { passive: false });
    document.addEventListener('keydown', preventKeyboard, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventWheel);
      document.removeEventListener('touchstart', preventTouch);
      document.removeEventListener('touchmove', preventTouch);
      document.removeEventListener('keydown', preventKeyboard);
    };
  }, []);

  // Handle pending scroll
  useEffect(() => {
    if (pendingScrollTarget !== null) {
      const targetRef = sectionRefs.current[pendingScrollTarget];
      if (targetRef) {
        setTimeout(() => {
          scrollToSection(pendingScrollTarget);
          setPendingScrollTarget(null);
        }, 50);
      }
    }
  }, [unlockedSections, pendingScrollTarget]);

  // Report progress
  useEffect(() => {
    if (config && onProgress) {
      const answeredCount = Object.keys(answers).length;
      const totalQuestions = config.questions.filter(q => q.required !== false).length;
      onProgress({
        current: answeredCount,
        total: totalQuestions,
        percent: Math.round((answeredCount / totalQuestions) * 100),
      });
    }
  }, [answers, config, onProgress]);

  const scrollToSection = useCallback((sectionIndex: number) => {
    const targetRef = sectionRefs.current[sectionIndex];
    if (targetRef) {
      isScrollingProgrammatically.current = true;
      targetRef.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setCurrentSection(sectionIndex);
      setTimeout(() => {
        isScrollingProgrammatically.current = false;
      }, 500);
    }
  }, []);

  const unlockAndScrollTo = useCallback((sectionIndex: number) => {
    setUnlockedSections(prev => new Set([...Array.from(prev), sectionIndex]));
    setPendingScrollTarget(sectionIndex);
  }, []);

  const handleAnswerChange = useCallback((questionId: string, value: string) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value,
    }));
  }, []);

  const isQuestionAnswered = (question: SurveyQuestion): boolean => {
    const answer = answers[question.id];
    if (question.required !== false) {
      return answer !== undefined && answer !== '';
    }
    return true;
  };

  const handleComplete = async () => {
    if (!config || isSubmitting) return;

    setIsSubmitting(true);

    const response: SurveyResponse = {
      surveyId: config.id,
      surveyVersion: config.version,
      completedAt: new Date().toISOString(),
      answers,
    };

    try {
      await onComplete(response);
      unlockAndScrollTo(totalSections - 1);
    } catch (error) {
      console.error('Error completing survey:', error);
      // Still show completion screen
      unlockAndScrollTo(totalSections - 1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAutoComplete = useCallback(() => {
    if (!config) return;

    // Generate answers for all questions
    const testAnswers = generateAllTestAnswers(config.questions);
    setAnswers(testAnswers);

    // Unlock all sections up to the last question (not the completion screen)
    const lastQuestionSection = config.questions.length; // questions are sections 1 to N
    const sectionsToUnlock = Array.from({ length: lastQuestionSection + 1 }, (_, i) => i);
    setUnlockedSections(new Set(sectionsToUnlock));

    // Scroll to the last question section
    setPendingScrollTarget(lastQuestionSection);
  }, [config]);

  const renderQuestion = (question: SurveyQuestion, questionIndex: number) => {
    const sectionIndex = questionIndex + 1; // +1 because intro is section 0
    const isLastQuestion = questionIndex === (config?.questions.length || 0) - 1;

    return (
      <div
        key={question.id}
        ref={el => { sectionRefs.current[sectionIndex] = el; }}
        className={`survey-section question-section ${currentSection === sectionIndex ? 'active' : ''}`}
      >
        <div className="section-content">
          <div className="question-header">
            <div className="question-pills">
              {question.categoryId && config?.categories && (
                <span className="category-badge">
                  {config.categories.find(c => c.id === question.categoryId)?.name}
                </span>
              )}
              <span className="question-number">
                Question {questionIndex + 1} of {config?.questions.length}
              </span>
            </div>
            <h2>{question.text}</h2>
            {question.required === false && <span className="optional-label">(Optional)</span>}
          </div>

          <div className="question-body">
            {question.type === 'emoji-scale' && (
              <EmojiScaleQuestion
                questionId={question.id}
                value={answers[question.id]}
                onChange={(value) => handleAnswerChange(question.id, value)}
                options={question.options}
              />
            )}

            {question.type === 'textarea' && (
              <TextareaQuestion
                questionId={question.id}
                value={answers[question.id]}
                onChange={(value) => handleAnswerChange(question.id, value)}
                placeholder={question.placeholder}
              />
            )}

            {question.type === 'radio' && question.options && (
              <div className="radio-options">
                {question.options.map((option) => (
                  <label key={option.value} className="radio-option">
                    <input
                      type="radio"
                      name={question.id}
                      value={option.value}
                      checked={answers[question.id] === option.value}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                    />
                    <span className="radio-checkmark"></span>
                    <span className="option-text">{option.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="section-navigation">
            {currentSection > 0 && (
              <button
                className="nav-btn back-btn"
                onClick={() => scrollToSection(currentSection - 1)}
              >
                ← Back
              </button>
            )}
            {(isQuestionAnswered(question) || question.required === false) && (
              <button
                className="nav-btn next-btn"
                onClick={() => {
                  if (isLastQuestion) {
                    handleComplete();
                  } else {
                    unlockAndScrollTo(currentSection + 1);
                  }
                }}
                disabled={isSubmitting}
              >
                {isLastQuestion ? (isSubmitting ? 'Submitting...' : 'Complete Survey') : 'Next →'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!config) {
    return (
      <div className="survey-container">
        <div className="survey-section active">
          <div className="section-content">
            <h1>Survey Not Found</h1>
            <p>The requested survey could not be loaded.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`survey-layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Mobile menu toggle */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setIsMobileSidebarOpen(true)}
        aria-label="Open navigation"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Floating expand button when sidebar is collapsed */}
      <button
        className="sidebar-expand-btn"
        onClick={() => setIsSidebarCollapsed(false)}
        aria-label="Open navigation"
      >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
        </svg>
      </button>

      {/* Sidebar Navigation */}
      <SurveySidebar
        config={config}
        currentSection={currentSection}
        unlockedSections={unlockedSections}
        answers={answers}
        onNavigate={scrollToSection}
        isMobileOpen={isMobileSidebarOpen}
        onMobileClose={() => setIsMobileSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className="survey-main-content">
        <div className="survey-container climate-survey">
          {/* Section 0: Intro */}
          {unlockedSections.has(0) && (
            <div
              ref={el => { sectionRefs.current[0] = el; }}
              className={`survey-section intro-section ${currentSection === 0 ? 'active' : ''}`}
            >
              <div className="section-content">
                <h1>{config.branding.introTitle}</h1>
                <p className="intro-description">{config.branding.introDescription}</p>
                {config.metadata?.estimatedMinutes && (
                  <p className="time-estimate">
                    Estimated time: {config.metadata.estimatedMinutes} minutes
                  </p>
                )}
                <div className="intro-buttons">
                  <button
                    className="cta-btn"
                    onClick={() => unlockAndScrollTo(1)}
                  >
                    Start Survey
                  </button>
                  {isDebugMode() && (
                    <button
                      className="cta-btn auto-complete-btn"
                      onClick={handleAutoComplete}
                    >
                      Auto-complete
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Question Sections */}
          {config.questions.map((question, index) => {
            const sectionIndex = index + 1;
            return unlockedSections.has(sectionIndex) ? renderQuestion(question, index) : null;
          })}

          {/* Completion Section */}
          {unlockedSections.has(totalSections - 1) && (
            <div
              ref={el => { sectionRefs.current[totalSections - 1] = el; }}
              className={`survey-section complete-section ${currentSection === totalSections - 1 ? 'active' : ''}`}
            >
              <div className="section-content">
                <div className="complete-icon">✓</div>
                <h1>{config.branding.completionTitle}</h1>
                <p>{config.branding.completionMessage}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SurveyRenderer;
